import React, { useMemo, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { visibleMenu, menuLeaves, canAccessLeaf, defaultMenuItem } from '../config/menuData.js';
import ReportDueBanner from '../components/ReportDueBanner.jsx';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import './AppShell.scss';

const APP_NAME = 'TugOS';
const COLLAPSE_KEY = 'harbortug.sidebar.collapsed';

/**
 * Application shell — a collapsible left sidebar (role-filtered nav) plus a slim
 * topbar with the account menu. The nav is built from `visibleMenu`, which only
 * surfaces pages the signed-in user may reach, so links never lead to a 403.
 * Pages render into the <Outlet/>.
 */
function SidebarLink({ leaf, onNavigate }) {
  return (
    <NavLink
      to={`/${leaf.slug}`}
      className={({ isActive }) => `shell-link${isActive ? ' active' : ''}`}
      title={leaf.label}
      onClick={onNavigate}
    >
      <span className="icon">
        <i className={leaf.icon || 'fa fa-circle'} aria-hidden="true" />
      </span>
      <span className="label">{leaf.label}</span>
    </NavLink>
  );
}

export default function AppShell() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === '1'
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  const roles = user?.roles || [];
  const menu = useMemo(() => visibleMenu({ roles }), [user]);

  // Mobile bottom-tab bar (<lg): Dashboard + the first few pages this role can
  // reach, then a "Menu" tab that opens the full drawer. Same RBAC filter as the
  // sidebar, so a tab never leads to a 403.
  const bottomTabs = useMemo(() => {
    const leaves = menuLeaves.filter(
      (l) => l.slug !== 'dashboard' && canAccessLeaf(l, { roles })
    );
    return [{ ...defaultMenuItem, icon: 'fa fa-gauge-high' }, ...leaves.slice(0, 4)];
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the group that contains the active route; others start collapsed.
  const activeGroup = useMemo(() => {
    for (const item of menu) {
      if (item.children?.some((c) => location.pathname === `/${c.slug}`)) return item.label;
    }
    return null;
  }, [menu, location.pathname]);
  const [openGroups, setOpenGroups] = useState({});
  const isGroupOpen = (label) => openGroups[label] ?? label === activeGroup;

  const toggleCollapse = () => {
    setCollapsed((v) => {
      localStorage.setItem(COLLAPSE_KEY, v ? '0' : '1');
      return !v;
    });
  };

  // Leave the page before clearing the session, so the route guard is less likely
  // to remember this page as the place to return to. The login page also checks
  // that any remembered page is one the next user's role can open.
  const handleLogout = () => {
    navigate('/login', { replace: true, state: null });
    logout();
  };

  const closeMobile = () => setMobileOpen(false);

  // A login still on its issued temporary password must choose its own first (8866).
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  const shellClass = `shell${collapsed ? ' collapsed' : ''}${mobileOpen ? ' mobile-open' : ''}`;

  return (
    <div className={shellClass}>
      <aside className="shell-sidebar">
        <div className="shell-brand">
          <span className="badge-square">
            <i className="fa fa-anchor" aria-hidden="true" />
          </span>
          <span className="shell-brand-text">{APP_NAME}</span>
        </div>

        <nav className="shell-nav">
          {menu.map((item) =>
            item.children && item.children.length ? (
              <div key={item.label}>
                <button
                  type="button"
                  className={`shell-group-header${isGroupOpen(item.label) ? ' open' : ''}`}
                  title={item.label}
                  onClick={() => {
                    // Collapsed rail: a group icon expands the sidebar and opens
                    // that group (so collapsed mirrors expanded — same items, no
                    // flattened child list leaking every page).
                    if (collapsed) {
                      setCollapsed(false);
                      localStorage.setItem(COLLAPSE_KEY, '0');
                      setOpenGroups((g) => ({ ...g, [item.label]: true }));
                    } else {
                      setOpenGroups((g) => ({ ...g, [item.label]: !isGroupOpen(item.label) }));
                    }
                  }}
                >
                  <i className={item.icon} aria-hidden="true" />
                  <span className="label">{item.label}</span>
                  <i className="fa fa-chevron-right chevron" aria-hidden="true" />
                </button>
                {!collapsed &&
                  isGroupOpen(item.label) &&
                  item.children.map((c) => (
                    <SidebarLink key={c.slug} leaf={c} onNavigate={closeMobile} />
                  ))}
              </div>
            ) : (
              <SidebarLink key={item.slug} leaf={item} onNavigate={closeMobile} />
            )
          )}
        </nav>
      </aside>

      <button
        type="button"
        className="shell-backdrop"
        aria-label="Close navigation"
        onClick={closeMobile}
      />

      <div className="shell-main">
        <header className="shell-topbar">
          <button
            type="button"
            className="shell-toggle d-none d-lg-inline-flex"
            aria-label="Toggle sidebar"
            onClick={toggleCollapse}
          >
            <i className="fa fa-bars" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="shell-toggle d-lg-none"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
          >
            <i className="fa fa-bars" aria-hidden="true" />
          </button>

          <div className="ms-auto">
            {isAuthenticated ? (
              <div className="dropdown">
                <button
                  className="btn btn-light btn-sm dropdown-toggle d-flex align-items-center"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="fa fa-user-circle me-2" aria-hidden="true" />
                  <span>{user?.name || user?.email || 'Account'}</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <span className="dropdown-item-text text-muted small">
                      {user?.email}
                      {user?.role ? ` · ${user.role}` : ''}
                    </span>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <Link className="dropdown-item" to="/change-password" data-testid="menu-change-password">
                      <i className="fa fa-key me-2" aria-hidden="true" />
                      Change Password
                    </Link>
                  </li>
                  <li>
                    <button className="dropdown-item" type="button" onClick={handleLogout}>
                      <i className="fa fa-sign-out-alt me-2" aria-hidden="true" />
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <Link className="btn btn-primary btn-sm" to="/login" state={{ from: location }}>
                <i className="fa fa-sign-in-alt me-2" aria-hidden="true" />
                Login
              </Link>
            )}
          </div>
        </header>

        <main className="shell-content">
          <ReportDueBanner />
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>

        <footer className="text-center text-muted py-4">
          <small>&copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.</small>
        </footer>
      </div>

      {/* Mobile bottom-tab bar — hidden on desktop (≥lg). */}
      <nav className="shell-bottomnav d-lg-none" aria-label="Primary">
        {bottomTabs.map((tab) => (
          <NavLink
            key={tab.slug}
            to={`/${tab.slug}`}
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={closeMobile}
          >
            <i className={tab.icon || 'fa fa-circle'} aria-hidden="true" />
            <span>{tab.label.replace(/ .*/, '')}</span>
          </NavLink>
        ))}
        <button type="button" onClick={() => setMobileOpen(true)}>
          <i className="fa fa-ellipsis-h" aria-hidden="true" />
          <span>Menu</span>
        </button>
      </nav>
    </div>
  );
}
